import { useId, useState } from "react";
import { ChevronDown } from "lucide-react";
import type {
  DirectoryFieldPreferenceKey,
  DirectoryProfilePreview,
  DirectoryPrivacyPreferences,
} from "@shared/directoryPrivacy";
import PersonPortraitAvatar from "@/components/PersonPortraitAvatar";

type DirectoryPrivacyControlsProps = {
  value: DirectoryPrivacyPreferences;
  onChange: (next: DirectoryPrivacyPreferences) => void;
  preview: DirectoryProfilePreview;
  disabled?: boolean;
  /**
   * Collapses the ten per-field switches behind a disclosure.
   *
   * Opt-in rather than the default because it is only right where privacy is a
   * step inside a longer task — signup, where ten switches between the visitor
   * and the next button is the bulk of the form. Where managing visibility IS
   * the task (Profile Settings, the admin drawer), hiding the switches hides
   * the point of the screen.
   */
  collapsibleFields?: boolean;
};

const FIELD_OPTIONS: Array<{
  key: DirectoryFieldPreferenceKey;
  label: string;
  description: string;
}> = [
  { key: "showTitle", label: "Show title", description: "Your professional role" },
  { key: "showCompany", label: "Show company", description: "Your organization" },
  { key: "showIndustry", label: "Show industry", description: "Your professional field" },
  { key: "showInterests", label: "Show interests", description: "Topics you selected" },
  { key: "showBio", label: "Show bio", description: "Your profile introduction" },
  { key: "showLocation", label: "Show location", description: "Your city or region" },
  { key: "showLinkedIn", label: "Show LinkedIn", description: "Your LinkedIn profile link" },
  { key: "showEmail", label: "Show email", description: "Your email address" },
  { key: "showPhone", label: "Show phone", description: "Your phone number" },
];

function Toggle({
  checked,
  disabled,
  label,
  description,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  label: string;
  description: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "1rem",
        padding: ".75rem .8rem",
        borderRadius: ".65rem",
        background: checked ? "rgba(233,30,140,.06)" : "rgba(255,255,255,.025)",
        border: `1px solid ${checked ? "rgba(233,30,140,.2)" : "rgba(255,255,255,.07)"}`,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.58 : 1,
      }}
    >
      <span>
        <strong style={{ display: "block", color: "#E2E8F0", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".76rem" }}>
          {label}
        </strong>
        <span style={{ display: "block", marginTop: ".14rem", color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: ".68rem" }}>
          {description}
        </span>
      </span>
      <input
        type="checkbox"
        aria-label={label}
        checked={checked}
        disabled={disabled}
        onChange={event => onChange(event.target.checked)}
        style={{ width: 18, height: 18, accentColor: "#E91E8C", flexShrink: 0 }}
      />
    </label>
  );
}

/**
 * A field only reaches the preview card when the member has opted it in AND the
 * profile actually has a value for it. The `trim()` check is the same emptiness
 * test the flat preview list used, so turning a toggle on for a field the member
 * has not filled in still shows nothing rather than an empty row.
 */
function shown(enabled: boolean, raw: string | undefined) {
  const text = raw?.trim();
  return enabled && text ? text : undefined;
}

/** Small caps label above a group of preview values. */
function PreviewLabel({ children }: { children: string }) {
  return (
    <span
      style={{
        display: "block",
        color: "rgba(148,163,184,.85)",
        fontFamily: "'Space Grotesk', sans-serif",
        fontSize: ".58rem",
        letterSpacing: ".1em",
        textTransform: "uppercase",
      }}
    >
      {children}
    </span>
  );
}

function PreviewChip({ children, accent }: { children: string; accent: string }) {
  return (
    <span
      style={{
        display: "inline-block",
        maxWidth: "100%",
        padding: ".2rem .5rem",
        borderRadius: "999px",
        background: `${accent}14`,
        border: `1px solid ${accent}3D`,
        color: accent,
        fontFamily: "'Inter', sans-serif",
        fontSize: ".68rem",
        lineHeight: 1.4,
        overflowWrap: "anywhere",
      }}
    >
      {children}
    </span>
  );
}

function PreviewContactRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "grid", gap: ".1rem", minWidth: 0 }}>
      <PreviewLabel>{label}</PreviewLabel>
      <span
        style={{
          color: "#CBD5E1",
          fontFamily: "'Inter', sans-serif",
          fontSize: ".72rem",
          overflowWrap: "anywhere",
        }}
      >
        {value}
      </span>
    </div>
  );
}

export default function DirectoryPrivacyControls({
  value,
  onChange,
  preview,
  disabled = false,
  collapsibleFields = false,
}: DirectoryPrivacyControlsProps) {
  const fieldsId = useId();
  const [fieldsOpen, setFieldsOpen] = useState(false);
  // With no disclosure there is nothing to expand, so the switches are simply
  // always present.
  const fieldsExpanded = !collapsibleFields || fieldsOpen;

  const setField = (key: DirectoryFieldPreferenceKey, checked: boolean) => {
    onChange({ ...value, [key]: checked });
  };

  // Same visibility rules as before — the master switch gates everything, each
  // field needs its own toggle, and empty values never render.
  const name = shown(true, preview.name);
  const title = shown(value.showTitle, preview.title);
  const company = shown(value.showCompany, preview.company);
  const industry = shown(value.showIndustry, preview.industry);
  const interests = shown(
    value.showInterests,
    preview.interests?.length ? preview.interests.join(", ") : undefined,
  );
  const bio = shown(value.showBio, preview.bio);
  // Ungated: a photo that has been uploaded is shown, so the preview shows it.
  const photoUrl = preview.photoUrl ?? undefined;
  const location = shown(value.showLocation, preview.location);
  const linkedinUrl = shown(value.showLinkedIn, preview.linkedinUrl);
  const email = shown(value.showEmail, preview.email);
  const phone = shown(value.showPhone, preview.phone);

  const interestChips = interests
    ? interests.split(",").map(entry => entry.trim()).filter(Boolean)
    : [];
  const hasTags = Boolean(industry) || Boolean(location) || interestChips.length > 0;
  const hasContact = Boolean(linkedinUrl) || Boolean(email) || Boolean(phone);

  return (
    <section aria-labelledby="directory-privacy-heading">
      <div style={{ marginBottom: "1rem" }}>
        <h3 id="directory-privacy-heading" style={{ margin: 0, color: "white", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".95rem" }}>
          Member Directory Privacy
        </h3>
        <p style={{ margin: ".45rem 0 0", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.6 }}>
          Non-paid members see your name only. Paid members see only the fields you select below. Email and phone stay private unless you turn them on. You can change these choices later in Profile Settings.
        </p>
      </div>

      <Toggle
        checked={value.directoryVisible}
        disabled={disabled}
        label="List me in the Member Directory"
        description="Turn this off to remove your entire listing"
        onChange={directoryVisible => onChange({ ...value, directoryVisible })}
      />

      {collapsibleFields && (
        <button
          type="button"
          onClick={() => setFieldsOpen(open => !open)}
          aria-expanded={fieldsOpen}
          aria-controls={fieldsId}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: ".75rem",
            width: "100%",
            margin: ".7rem 0 0",
            padding: ".7rem .8rem",
            borderRadius: ".65rem",
            background: "rgba(255,255,255,.025)",
            border: "1px solid rgba(255,255,255,.07)",
            color: "#E2E8F0",
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: ".76rem",
            fontWeight: 700,
            textAlign: "left",
            cursor: "pointer",
          }}
        >
          <span>
            Show additional privacy options
            <span style={{ display: "block", marginTop: ".14rem", color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: ".68rem", fontWeight: 400 }}>
              Choose which {FIELD_OPTIONS.length} profile fields paid members can see
            </span>
          </span>
          <ChevronDown
            size={18}
            aria-hidden="true"
            style={{
              flexShrink: 0,
              color: "#94A3B8",
              transform: fieldsOpen ? "rotate(180deg)" : "none",
              transition: "transform .18s ease",
            }}
          />
        </button>
      )}

      <div id={collapsibleFields ? fieldsId : undefined} hidden={!fieldsExpanded}>
        <p style={{ margin: ".7rem 0", color: "#67E8F9", fontFamily: "'Inter', sans-serif", fontSize: ".7rem" }}>
          Name is always shown while your listing is on.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: ".5rem" }}>
          {FIELD_OPTIONS.map(option => (
            <Toggle
              key={option.key}
              checked={value[option.key]}
              disabled={disabled}
              label={option.label}
              description={option.description}
              onChange={checked => setField(option.key, checked)}
            />
          ))}
        </div>
      </div>

      <style>{`
        .dpc-preview-card {
          transition: border-color .25s ease, box-shadow .25s ease, transform .25s ease;
        }
        .dpc-preview-card:hover {
          border-color: rgba(233,30,140,.34);
          box-shadow: 0 18px 44px rgba(2,6,23,.6), 0 0 0 1px rgba(233,30,140,.12) inset;
          transform: translateY(-1px);
        }
        @media (prefers-reduced-motion: reduce) {
          .dpc-preview-card { transition: none; }
          .dpc-preview-card:hover { transform: none; }
        }
      `}</style>

      <div
        role="region"
        aria-label="Paid member directory preview"
        style={{ marginTop: "1rem", minWidth: 0 }}
      >
        <strong style={{ display: "block", color: "#67E8F9", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".7rem", letterSpacing: ".08em", textTransform: "uppercase" }}>
          Paid member preview
        </strong>

        <div
          className="dpc-preview-card"
          style={{
            marginTop: ".6rem",
            padding: "1rem",
            minWidth: 0,
            borderRadius: "1rem",
            background:
              "linear-gradient(155deg, rgba(255,255,255,.07) 0%, rgba(13,18,32,.72) 58%, rgba(13,18,32,.86) 100%)",
            border: "1px solid rgba(255,255,255,.12)",
            backdropFilter: "blur(14px) saturate(130%)",
            WebkitBackdropFilter: "blur(14px) saturate(130%)",
            boxShadow: "0 14px 36px rgba(2,6,23,.48)",
            overflow: "hidden",
          }}
        >
          {!value.directoryVisible ? (
            <p style={{ margin: 0, color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.6 }}>
              Your profile will not appear in the directory.
            </p>
          ) : (
            <div style={{ display: "grid", gap: ".85rem", minWidth: 0 }}>
              {/* Identity: avatar, then the name as the loudest thing on the card. */}
              <div style={{ display: "flex", alignItems: "center", gap: ".7rem", minWidth: 0 }}>
                {/* Initials come from the name, so with no name there is nothing
                    for the avatar to stand in for. The photo answers to the
                    Show photo toggle above: a preview that ignored it would tell
                    the member the opposite of what the directory will do. */}
                {name ? (
                  <PersonPortraitAvatar
                    name={name}
                    portraitUrl={photoUrl}
                    size={46}
                    accent="#E91E8C"
                  />
                ) : null}
                <div style={{ minWidth: 0 }}>
                  {name ? (
                    <span
                      style={{
                        display: "block",
                        color: "white",
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontSize: "1.02rem",
                        fontWeight: 700,
                        letterSpacing: "-.01em",
                        lineHeight: 1.2,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {name}
                    </span>
                  ) : null}
                  {title || company ? (
                    <span
                      style={{
                        display: "block",
                        marginTop: ".18rem",
                        color: "#F5A623",
                        fontFamily: "'Inter', sans-serif",
                        fontSize: ".74rem",
                        lineHeight: 1.45,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {[title, company].filter(Boolean).join(" · ")}
                    </span>
                  ) : null}
                </div>
              </div>

              {bio ? (
                <p
                  style={{
                    margin: 0,
                    color: "#CBD5E1",
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "var(--chi-body-compact)",
                    lineHeight: 1.65,
                    overflowWrap: "anywhere",
                  }}
                >
                  {bio}
                </p>
              ) : null}

              {hasTags ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: ".35rem", minWidth: 0 }}>
                  {industry ? <PreviewChip accent="#00D4FF">{industry}</PreviewChip> : null}
                  {location ? <PreviewChip accent="#94A3B8">{location}</PreviewChip> : null}
                  {interestChips.map((interest, index) => (
                    <PreviewChip key={`${interest}-${index}`} accent="#E91E8C">
                      {interest}
                    </PreviewChip>
                  ))}
                </div>
              ) : null}

              {hasContact ? (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))",
                    gap: ".55rem",
                    paddingTop: ".7rem",
                    borderTop: "1px solid rgba(255,255,255,.09)",
                    minWidth: 0,
                  }}
                >
                  {linkedinUrl ? <PreviewContactRow label="LinkedIn" value={linkedinUrl} /> : null}
                  {email ? <PreviewContactRow label="Email" value={email} /> : null}
                  {phone ? <PreviewContactRow label="Phone" value={phone} /> : null}
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
